import type { THosStatus } from "../interfaces/statues";
import { DisjointIntervalSet, type IInterval } from "./disjointIntervalSet";

/**
 * Interface for log entries with duty status and timestamp
 */
interface IHosStatusAndDate {
    status: THosStatus;
    dateTime: Date;
}

/**
 * Tracks duty status period intervals by converting logs into time intervals and providing
 * functionality to query intervals within specified constraints.
 *
 * This class processes a sequence of duty status logs and creates time intervals
 * for managed statuses, filtering them by optional duration constraints.
 */
export default class DutyStatusIntervalTracker {
    private readonly disjointIntervalSet: DisjointIntervalSet;
    private readonly managedStatusesSet: Set<THosStatus>;

    /**
     * Creates periods from logs by processing consecutive log entries.
     * Each log creates a period that ends when the next log begins.
     * If endTime is provided, the last log extends to that time.
     *
     * @param logs Array of duty status logs, must be chronologically ordered
     * @param endTime Optional end time for the final period
     */
    // todo: should probably add infinite off before the first log. Interval of [-Infinity, logs[0].dateTime.getTime()];
    private createPeriodsFromLogs(logs: IHosStatusAndDate[], endTime?: number): void {
        if (!logs || logs.length === 0) {
            return;
        }

        // Process intervals between consecutive logs
        for (let i = 0; i < logs.length - 1; i++) {
            const log = logs[i];
            if (this.managedStatusesSet.has(log.status)) {
                const nextLog = logs[i + 1];
                this.disjointIntervalSet.insert({
                    start: log.dateTime.getTime(),
                    end: nextLog.dateTime.getTime()
                });
            }
        }

        // Handle the final log period if endTime is provided
        if (logs.length >= 1 && endTime) {
            const lastLog = logs[logs.length - 1];
            if (this.managedStatusesSet.has(lastLog.status)) {
                this.disjointIntervalSet.insert({
                    start: lastLog.dateTime.getTime(),
                    end: endTime
                });
            }
        }
    }

    /**
     * Checks if an interval meets the duration criteria (min/max duration constraints)
     *
     * @param interval The interval to check
     * @returns true if the interval duration qualifies based on min/max constraints
     */
    private doesIntervalDurationQualify(interval: IInterval): boolean {
        const duration = interval.end - interval.start;
        return (!this.minDuration || duration >= this.minDuration) && (!this.maxDuration || duration <= this.maxDuration);
    }

    /**
     * Creates a new DutyStatusIntervalTracker instance
     *
     * @param logs Array of duty status logs, must be chronologically ordered
     * @param managedStatuses Array of status types to track (only these statuses create intervals)
     * @param minDuration Optional minimum duration filter in milliseconds
     * @param maxDuration Optional maximum duration filter in milliseconds
     * @param endTime Optional end time for the final period in milliseconds since epoch
     *
     * @throws {Error} When input validation fails
     */
    constructor(
        logs: IHosStatusAndDate[],
        managedStatuses: THosStatus[],
        private readonly minDuration?: number,
        private readonly maxDuration?: number,
        endTime?: number
    ) {
        // Input validation
        if (!Array.isArray(logs)) {
            throw new Error("logs must be an array");
        }
        if (!Array.isArray(managedStatuses)) {
            throw new Error("managedStatuses must be an array");
        }
        if (minDuration !== undefined && minDuration < 0) {
            throw new Error("minDuration cannot be negative");
        }
        if (maxDuration !== undefined && maxDuration < 0) {
            throw new Error("maxDuration cannot be negative");
        }
        if (minDuration !== undefined && maxDuration !== undefined && minDuration > maxDuration) {
            throw new Error("minDuration cannot be greater than maxDuration");
        }
        if (endTime !== undefined && endTime < 0) {
            throw new Error("endTime cannot be negative");
        }

        // Validate log entries
        for (let i = 0; i < logs.length; i++) {
            const log = logs[i];
            if (!log || typeof log.status !== "string" || !(log.dateTime instanceof Date) || isNaN(log.dateTime.getTime())) {
                throw new Error(`Invalid log entry at index ${i}: must have valid status and dateTime`);
            }
        }

        this.disjointIntervalSet = new DisjointIntervalSet();
        this.managedStatusesSet = new Set(managedStatuses);
        this.createPeriodsFromLogs(logs, endTime);
    }

    /**
     * Returns all intervals within the specified time window that meet the duration criteria.
     *
     * @param trimToInterval The time window to query within
     * @returns Array of intervals that fall within the time window and meet duration constraints
     */
    getQualifiedIntervals(trimToInterval: IInterval): IInterval[] {
        return this.disjointIntervalSet.queryAndTrim(trimToInterval).filter(interval => this.doesIntervalDurationQualify(interval));
    }

    /**
     * Returns all intervals within the specified time window without applying duration filters.
     *
     * @param trimToInterval The time window to query within
     * @returns Array of all intervals that fall within the time window
     */
    getAllIntervals(trimToInterval: IInterval): IInterval[] {
        return this.disjointIntervalSet.queryAndTrim(trimToInterval);
    }

    /**
     * Calculates the total duration of all intervals in the provided list.
     *
     * @param intervals Array of intervals to calculate total duration for
     * @returns Total duration in milliseconds of all intervals
     */
    static getTotalDuration(intervals: IInterval[]): number {
        return intervals.reduce((total, interval) => total + (interval.end - interval.start), 0);
    }
}
